import React from "react";
import { Link } from "react-router-dom";
import { useSettings, CATEGORIES } from "../context/SettingsContext";
import { Instagram, Phone, Mail, Lock } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { api } from "../lib/api";

function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.515 5.26l-.999 3.648 3.973-1.045zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
    </svg>
  );
}

export default function Footer() {
  const { settings } = useSettings();
  const { toast } = useToast();

  const onSubscribe = async (e) => {
    e.preventDefault();
    const email = e.target.elements.email.value;
    if (!email) return;
    try {
      const res = await api.subscribe(email);
      toast({
        title: res.already ? "כבר רשום" : "נרשמת בהצלחה",
        description: res.already
          ? "המייל הזה כבר נמצא ברשימת התפוצה."
          : `תודה שהצטרפת לרשימת ${settings.brandName}.`,
      });
      e.target.reset();
    } catch (err) {
      toast({ title: "שגיאה", description: "אנא בדוק את כתובת המייל ונסה שוב." });
    }
  };

  return (
    <footer className="mt-20 border-t border-neutral-200 bg-white">
      <div className="border-b border-neutral-200">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-14 grid gap-10 md:grid-cols-2 items-center">
          <div>
            <p className="text-xs tracking-[0.3em] text-neutral-400">NEWSLETTER</p>
            <h3 className="font-display text-2xl md:text-3xl mt-3 tracking-wide">
              Subscribe for exclusive drops
            </h3>
            <p className="text-sm text-neutral-500 mt-2 max-w-md">
              הירשמו לקבלת עדכונים, גישה למבצעים בלעדיים ועוד.
            </p>
          </div>
          <form onSubmit={onSubscribe} className="flex w-full max-w-md md:ml-auto">
            <input
              name="email"
              type="email"
              required
              placeholder="Your email address"
              className="flex-1 border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-black transition-colors"
            />
            <button
              type="submit"
              className="bg-black text-white px-7 py-3 text-xs tracking-[0.2em] hover:bg-neutral-800 transition-colors"
            >
              JOIN
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-1">
          <Link to="/" className="font-display text-2xl tracking-[0.4em]">{settings.brandName}</Link>
          <p className="text-sm text-neutral-500 mt-4 leading-relaxed">
            Elevated essentials &amp; statement pieces. Designed to be loved.
          </p>
          <div className="flex items-center gap-4 mt-5">
            <a href={`https://wa.me/${settings.whatsapp}`} target="_blank" rel="noreferrer" className="text-neutral-600 hover:text-black">
              <WhatsAppIcon className="h-5 w-5" />
            </a>
            <a href={settings.instagram} target="_blank" rel="noreferrer" className="text-neutral-600 hover:text-black">
              <Instagram className="h-5 w-5" />
            </a>
          </div>
        </div>

        <div>
          <h4 className="text-xs tracking-[0.25em] text-neutral-900 mb-4">QUICK MENU</h4>
          <ul className="space-y-2.5 text-sm text-neutral-500">
            <li><Link to="/" className="hover:text-black">Home</Link></li>
            <li><Link to="/shop" className="hover:text-black">Shop All</Link></li>
            <li><Link to="/shop?tag=new-drop" className="hover:text-black">New Drop</Link></li>
            <li><Link to="/shop?tag=best-seller" className="hover:text-black">Best Seller</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs tracking-[0.25em] text-neutral-900 mb-4">CATEGORIES</h4>
          <ul className="space-y-2.5 text-sm text-neutral-500">
            {CATEGORIES.map((c) => (
              <li key={c.slug}>
                <Link to={`/shop/${c.slug}`} className="hover:text-black">{c.name}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-xs tracking-[0.25em] text-neutral-900 mb-4">CONTACT</h4>
          <ul className="space-y-2.5 text-sm text-neutral-500">
            <li className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              <a href={`tel:${settings.whatsapp}`} className="hover:text-black">{settings.whatsappDisplay}</a>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <a href={`mailto:${settings.email}`} className="hover:text-black">{settings.email}</a>
            </li>
            <li><Link to="/about" className="hover:text-black">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-black">Contact</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-neutral-200">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-5 flex flex-col gap-3 md:flex-row items-center justify-between">
          <p className="text-xs text-neutral-400 order-2 md:order-1 flex items-center gap-2">
            © {new Date().getFullYear()} {settings.brandName}. All rights reserved.
            <Link to="/admin" aria-label="Admin" title="ניהול" className="text-neutral-300 hover:text-neutral-700 transition-colors">
              <Lock className="h-3.5 w-3.5" />
            </Link>
          </p>
          <div className="order-1 md:order-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-neutral-500">
            <Link to="/privacy" className="hover:text-black">מדיניות פרטיות</Link>
            <span className="text-neutral-300">·</span>
            <Link to="/terms" className="hover:text-black">תקנון</Link>
            <span className="text-neutral-300">·</span>
            <Link to="/shipping" className="hover:text-black">משלוחים והחזרות</Link>
            <span className="text-neutral-300">·</span>
            <Link to="/accessibility" className="hover:text-black">הצהרת נגישות</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
